import { createAction, props } from '@ngrx/store';
import { Map } from 'immutable';
import { AppMetadataEntity } from 'src/app/ui/components/app-metadata/app-metadata.entity';

import {
	CharacteristicType,
	DcTimestampFormat,
} from '../../core/dc-core-types';
import { AvailableVersionEntity } from '../components/change-version-modal/available-version.entity';
import { DepotHeaderItemEntity } from '../components/create-entity-from-depot-modal/depot-header-item.entity';
import { DepotHeaderListEntity } from '../components/create-entity-from-depot-modal/depot-header-list.entity';
import { SubscriptionForDepotItemEntity } from '../components/depot-creation-modal/available-subscription-item.entity';
import { SubscriptionListEntity } from '../components/depot-creation-modal/available-subscriptions-list.entity';
import { DepotListEntity } from '../components/depot-list/depot-list.entity';
import { DepotSchemaEntity } from '../components/depot-schema-viewer/depot-schema.entity';
import { DepotSchemaConfig } from '../domain/depot-schema-config';
import { DataLinkRepository } from '../domain/depot.types';
import { SupportedLanguageEntity } from '../domain/supported-language.entity';

export enum DepotActions {
	InitDepotList = '[Depot] get depot list',
	ResetAvailableHeaders = '[Depot] close create depot entity modal',
	RefreshDepotList = '[Depot] refresh Depot list',
	UpdateEntitiesCountForDepot = '[Depot] get entity count for depots',
	UpdateEntitiesCountForDepotSuccess = '[BE][Depot] get entity count for depots success',
	UpdateEntitiesCountForDepotFailure = '[BE][Depot] get entity count for depots failure',
	FetchDepotListFail = '[BE][Depot] fetch Depot list fail',
	FetchDepotListSuccess = '[BE][Depot] fetch Depot list success',
	NavigateToV1ListAndCreateEntity = '[Depot] Navigate to V1 list and create Entity from target depot',
	NavigateToDepotRoute = '[Depot] navigate to Depot route',
	NavigateToDepotCreateRoute = '[Depot] navigate to Depot create route',
	NavigateToDepotCreateModal = '[Depot] navigate to Depot create modal',
	OpenCreateDepotModal = '[Depot] open create Depot modal',
	NavigateToDepotDuplicateRoute = '[Depot] navigate to Depot duplicate route',
	OpenCreateDepotEntityModal = '[Depot] open V2 create entity from target depot modal',
	DeleteDepot = '[Depot] delete depot',
	BulkDeleteDepots = '[Depot] delete many depots',
	DeleteDepotSuccess = '[BE][Depot] delete depot success',
	DeleteDepotFail = '[BE][Depot] delete depot fail',
	BulkDeleteDepotsSuccess = '[BE][Depot] delete many depots success',
	BulkDeleteDepotsFail = '[BE][Depot] delete many depots fail',
	ToggleDepotStatus = '[Depot] toggle depot status',
	InitAvailableDepotHeadersForEntityCreation = '[Depot] init available depot headers for entity creation',
	FetchDepotHeadersSuccess = '[BE][Depot] fetch depot headers success',
	FetchDepotHeadersFail = '[BE][Depot] fetch depot headers fail',
	CreateEntityFromDepot = '[Depot] Create entity from depot',
	CreateEntityFromDepotSuccess = '[BE][Depot] Create entity from depot success',
	CreateEntityFromDepotFail = '[BE][Depot] Create entity from depot fail',
	OpenDepotHeaderFieldsSelection = '[Depot] open depot header fields selection',
	TriggerDetectAvailableHeaders = '[Depot] Trigger Schema Headers detection',
	ReplaceCurrentLabel = '[Depot] Replace header label search term with',
	ReconstructDepotSchemaMappingSuccess = '[Depot][Schema Mapping] Reconstruct headers schema for depot success',
	ReconstructDepotSchemaMappingError = '[Depot][Schema Mapping] Reconstruct headers schema for depot failure',
	FetchDepotSchemaMappingSuccess = '[BE][Depot][Schema Mapping] Get headers schema for depot success',
	FetchDepotSchemaMappingError = '[BE][Depot][Schema Mapping] Get headers schema for depot failure',
	InitDepotHeaderSetting = '[Depot][Schema Mapping] Init depot headers setting',
	GetDepotHeaderSettingSuccess = '[Depot] Get depot headers setting success',
	GetDepotHeaderSettingFail = '[Depot] Get depot headers setting fail',
	CloseCreateDepotModal = '[Depot] close creation modal',
	CreateSubscriptionForRepository = '[Depot] Create subscription for repository',
	CreateSubscriptionForRepositorySuccess = '[BE][Depot] Create subscription for repository success',
	CreateSubscriptionForRepositoryFail = '[BE][Depot] Create subscription for repository fail',
	FetchAvailableSubscriptionsForDepotCreation = '[BE][Depot] Fetch available subscriptions for depot creation',
	FetchAvailableSubscriptionsForDepotCreationSuccess = '[BE][Depot] successfully fetched subscriptions',
	FetchAvailableSubscriptionsForDepotCreationFailure = '[BE][Depot] failure while fetching subscriptions',
	InitDepotCreation = '[Depot] initialization on depot creation modal open',
	OpenChangeVersionModal = '[Depot] Open change version modal',
	GetAvailableVersionsForSelectedDataLinkRepo = '[Depot] Get available versions for selected DataLink repo',
	FetchAvailableVersionForSelectedDataLinkRepoSuccess = '[BE][Depot] Fetch available versions for selected DataLink repo success',
	FetchAvailableVersionForSelectedDataLinkRepoFail = '[BE][Depot] Fetch available versions for selected DataLink repo fail',
	ChangeVersionForSelectedDataLinkRepo = '[Depot] Change version for selected DataLink repo',
	ChangeVersionForSelectedDataLinkRepoSuccess = '[BE][Depot] Change version for selected DataLink repo success',
	ChangeVersionForSelectedDataLinkRepoFail = '[BE][Depot] Change version for selected DataLink repo fail',
	OpenSendEmailPopup = '[Depot] Open send email popup',
	ContactDataLinkEditorsSuccess = '[BE][Depot] Contact DataLink editors success',
	ContactDataLinkEditorsFail = '[BE][Depot] Contact DataLink editors fail',
}

export const initDepotList = createAction(DepotActions.InitDepotList);

export const refreshDepotList = createAction(DepotActions.RefreshDepotList);

export const fetchDepotListSuccess = createAction(
	DepotActions.FetchDepotListSuccess,
	props<{ depotList: DepotListEntity }>()
);

export const fetchDepotListFail = createAction(
	DepotActions.FetchDepotListFail,
	props<{ error: unknown }>()
);

export const updateEntitiesCountForDepot = createAction(
	DepotActions.UpdateEntitiesCountForDepot,
	props<{ ids: Array<number>; shouldReload: boolean }>()
);

export const updateEntitiesCountForDepotSuccess = createAction(
	DepotActions.UpdateEntitiesCountForDepotSuccess,
	props<{ depotList: DepotListEntity }>()
);

export const updateEntitiesCountForDepotFailure = createAction(
	DepotActions.UpdateEntitiesCountForDepotFailure,
	props<{ error: unknown }>()
);

export const navigateToDepot = createAction(
	DepotActions.NavigateToDepotRoute,
	props<{
		id: number;
		depotType: string;
	}>()
);
export const navigateToDepotCreate = createAction(
	DepotActions.NavigateToDepotCreateRoute
);
export const navigateToDepotCreateModal = createAction(
	DepotActions.NavigateToDepotCreateModal
);
export const openCreateDepotModal = createAction(
	DepotActions.OpenCreateDepotModal
);
export const navigateToDepotDuplicate = createAction(
	DepotActions.NavigateToDepotDuplicateRoute,
	props<{ id: number; depotType: string }>()
);

export const deleteDepot = createAction(
	DepotActions.DeleteDepot,
	props<{ depotId: number }>()
);

export const bulkDeleteDepots = createAction(
	DepotActions.BulkDeleteDepots,
	props<{ depotIds: Array<number> }>()
);

export const deleteDepotSuccess = createAction(DepotActions.DeleteDepotSuccess);

export const deleteDepotFail = createAction(DepotActions.DeleteDepotFail);

export const bulkDeleteDepotsSuccess = createAction(
	DepotActions.BulkDeleteDepotsSuccess
);

export const bulkDeleteDepotsFail = createAction(
	DepotActions.BulkDeleteDepotsFail
);

export const toggleDepotStatus = createAction(
	DepotActions.ToggleDepotStatus,
	props<{ depotId: number; depotType: string; status: boolean }>()
);

export const openCreateEntityFromDepotModal = createAction(
	DepotActions.OpenCreateDepotEntityModal,
	props<{ depotId: number; depotLabel: string; depotType: string }>()
);

export const resetAvailableHeaders = createAction(
	DepotActions.ResetAvailableHeaders
);

export const initAvailableDepotHeadersForEntityCreation = createAction(
	DepotActions.InitAvailableDepotHeadersForEntityCreation,
	props<{ depotId: number; depotType: string }>()
);

export const fetchDepotHeadersSuccess = createAction(
	DepotActions.FetchDepotHeadersSuccess,
	props<{
		headers: DepotHeaderListEntity;
		availableCharacteristicTypes: Map<number, CharacteristicType>;
		availableDateFormats: Map<string, DcTimestampFormat>;
		availableTimezones: Array<string>;
		supportedLanguages: Map<string, SupportedLanguageEntity>;
	}>()
);

export const fetchDepotHeadersFail = createAction(
	DepotActions.FetchDepotHeadersFail,
	props<{ error: unknown }>()
);

export const createEntityFromDepot = createAction(
	DepotActions.CreateEntityFromDepot,
	props<{
		depotId: number;
		metadata: AppMetadataEntity;
		headers: Array<DepotHeaderItemEntity>;
	}>()
);

export const createEntityFromDepotSuccess = createAction(
	DepotActions.CreateEntityFromDepotSuccess
);

export const createEntityFromDepotFail = createAction(
	DepotActions.CreateEntityFromDepotFail,
	props<{ error: unknown }>()
);

export const openDepotHeaderFieldsSelection = createAction(
	DepotActions.OpenDepotHeaderFieldsSelection,
	props<{ params: DepotSchemaConfig; currentSchema: unknown | null }>()
);

export const replaceCurrentLabelWith = createAction(
	DepotActions.ReplaceCurrentLabel,
	props<{ searchTerm: string; replaceWith: string }>()
);

export const triggerDetectAvailableHeaders = createAction(
	DepotActions.TriggerDetectAvailableHeaders,
	props<{ params: DepotSchemaConfig }>()
);

export const reconstructDepotSchemaMappingSuccess = createAction(
	DepotActions.ReconstructDepotSchemaMappingSuccess,
	props<{ mapping: Array<DepotSchemaEntity> }>()
);

export const reconstructDepotSchemaMappingError = createAction(
	DepotActions.ReconstructDepotSchemaMappingError,
	props<{ error: unknown }>()
);

export const fetchDepotSchemaMappingSuccess = createAction(
	DepotActions.FetchDepotSchemaMappingSuccess,
	props<{ mapping: Array<DepotSchemaEntity> }>()
);

export const fetchDepotSchemaMappingError = createAction(
	DepotActions.FetchDepotSchemaMappingError,
	props<{ error: unknown }>()
);

export const initDepotHeaderSetting = createAction(
	DepotActions.InitDepotHeaderSetting
);

export const getDepotHeaderSettingSuccess = createAction(
	DepotActions.GetDepotHeaderSettingSuccess,
	props<{
		availableCharacteristicTypes: Map<number, CharacteristicType>;
		availableDateFormats: Map<string, DcTimestampFormat>;
		availableTimezones: Array<string>;
		supportedLanguages: Map<string, SupportedLanguageEntity>;
	}>()
);

export const getDepotHeaderSettingFail = createAction(
	DepotActions.GetDepotHeaderSettingFail,
	props<{ error: unknown }>()
);

export const closeCreateDepotModal = createAction(
	DepotActions.CloseCreateDepotModal
);

export const createSubscriptionForRepository = createAction(
	DepotActions.CreateSubscriptionForRepository,
	props<{
		metadata: AppMetadataEntity;
		subscription: SubscriptionForDepotItemEntity;
		allowProducerVersionSwitch: boolean;
	}>()
);

export const createSubscriptionForRepositorySuccess = createAction(
	DepotActions.CreateSubscriptionForRepositorySuccess,
	props<{
		id: number;
		depotType: string;
	}>()
);

export const createSubscriptionForRepositoryFail = createAction(
	DepotActions.CreateSubscriptionForRepositoryFail,
	props<{
		error: unknown;
	}>()
);

export const fetchAvailableSubscriptionsForDepotCreation = createAction(
	DepotActions.FetchAvailableSubscriptionsForDepotCreation
);

export const fetchAvailableSubscriptionsForDepotCreationSuccess = createAction(
	DepotActions.FetchAvailableSubscriptionsForDepotCreationSuccess,
	props<{
		list: SubscriptionListEntity;
	}>()
);

export const fetchAvailableSubscriptionsForDepotCreationFailure = createAction(
	DepotActions.FetchAvailableSubscriptionsForDepotCreationFailure,
	props<{ error: unknown }>()
);

export const initDepotCreation = createAction(DepotActions.InitDepotCreation);

export const openChangeVersionModal = createAction(
	DepotActions.OpenChangeVersionModal,
	props<{ depot: DataLinkRepository }>()
);

export const getAvailableVersionsForSelectedDataLinkRepo = createAction(
	DepotActions.GetAvailableVersionsForSelectedDataLinkRepo,
	props<{ depotId: number }>()
);

export const fetchAvailableVersionForSelectedDataLinkRepoSuccess = createAction(
	DepotActions.FetchAvailableVersionForSelectedDataLinkRepoSuccess,
	props<{ versions: Map<number, AvailableVersionEntity> }>()
);

export const fetchAvailableVersionForSelectedDataLinkRepoFail = createAction(
	DepotActions.FetchAvailableVersionForSelectedDataLinkRepoFail,
	props<{ error: unknown }>()
);

export const changeVersionForSelectedDataLinkRepo = createAction(
	DepotActions.ChangeVersionForSelectedDataLinkRepo,
	props<{ depotId: number; newVersion: number }>()
);

export const changeVersionForSelectedDataLinkRepoSuccess = createAction(
	DepotActions.ChangeVersionForSelectedDataLinkRepoSuccess
);

export const changeVersionForSelectedDataLinkRepoFail = createAction(
	DepotActions.ChangeVersionForSelectedDataLinkRepoFail,
	props<{ error: unknown }>()
);

export const openSendEmailPopup = createAction(
	DepotActions.OpenSendEmailPopup,
	props<{ depotId: number }>()
);

export const contactDataLinkEditorsSuccess = createAction(
	DepotActions.ContactDataLinkEditorsSuccess
);

export const contactDataLinkEditorsFail = createAction(
	DepotActions.ContactDataLinkEditorsFail,
	props<{ error: unknown }>()
);
